import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api';
import useOnline from '../useOnline';
import BannerOffline from '../BannerOffline';
import { guardarViajes, leerViajes, contarCola, encolar, guardarClientes, leerClientes } from '../offlineDB';
import { programarReintentos, detenerReintentos } from '../syncQueue';

const ESTADO_CONFIG = {
  pendiente:  { label: 'Pendiente',  bg: '#e2e3e5', color: '#41464b' },
  aceptado:   { label: 'Aceptado',   bg: '#cff4fc', color: '#055160' },
  en_curso:   { label: 'En Curso',   bg: '#fff3cd', color: '#856404' },
  completado: { label: 'Completado', bg: '#d1e7dd', color: '#0a3622' },
  cancelado:  { label: 'Cancelado',  bg: '#f8d7da', color: '#721c24' },
};

const Viajes = ({ usuario, onLogout }) => {
  const [viajes, setViajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('lista'); // lista | nuevo | detalle | cerrar
  const [viajeActual, setViajeActual] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Catálogos
  const [vehiculos, setVehiculos] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [origenes, setOrigenes] = useState([]);
  const [tiposCombustible, setTiposCombustible] = useState([]);
  const [clientes, setClientes] = useState([]);

  // Form nuevo viaje
  const [formViaje, setFormViaje] = useState({
    vehiculo_id: '', conductor_id: '', origen_id: '',
    tipo_combustible_id: '', fecha_salida: '', galones_declarados: '', dieta: '', observaciones: ''
  });
  const [editandoId, setEditandoId] = useState(null);   // null = crear; id = editar

  // Entregas
  const [entregas, setEntregas] = useState([]);
  const [formEntrega, setFormEntrega] = useState({ cliente_id: '', galones_entregados: '', precio_galon: '', observaciones: '' });

  // Cerrar viaje
  const [formCerrar, setFormCerrar] = useState({ galones_reales: '', precio_galon_excedente: '', fecha_retorno: '', observaciones: '' });

  const esAdmin = usuario?.rol === 'admin';
  // ¿Puede ver/gestionar dinero? (precios, dieta, excedentes) → permiso excedentes.editar
  const permisos = usuario?.permisos;
  const puedeDinero = esAdmin || !!(permisos && permisos.excedentes && permisos.excedentes.editar);
  const esConductor = usuario?.rol === 'conductor';
  const esDespachador = usuario?.rol === 'despachador';

  // Offline (Capa 3): conexión + contador de pendientes en la cola
  const { online } = useOnline();
  const [pendientes, setPendientes] = useState(0);
  const refrescarPendientes = useCallback(async () => {
    try { setPendientes(await contarCola()); } catch { setPendientes(0); }
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // allSettled: si un catálogo da 403 (sin permiso), no rompe el resto.
      const [vRes, vehRes, condRes, origRes, tcRes, cliRes] = await Promise.allSettled([
        api.get('/viajes'),
        api.get('/vehiculos'),
        api.get('/conductores'),
        api.get('/origenes-carga'),
        api.get('/tipos-combustible'),
        api.get('/clientes'),
      ]);
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : []);
      // Viajes es lo esencial: si falla, sí es un error real.
      if (vRes.status === 'rejected') throw vRes.reason;
      const listaViajes = val(vRes);
      const listaClientes = val(cliRes);
      setViajes(listaViajes);
      setVehiculos(val(vehRes));
      setConductores(val(condRes));
      setOrigenes(val(origRes));
      setTiposCombustible(val(tcRes));
      setClientes(listaClientes);
      setError('');
      // Offline: guardar viajes y clientes en cache local para usarlos sin señal
      guardarViajes(listaViajes);
      if (listaClientes.length > 0) guardarClientes(listaClientes);
    } catch (err) {
      // Si falló por falta de conexión, intentar leer del cache local
      const esErrorDeRed = !err?.response;   // sin respuesta HTTP = problema de red
      if (esErrorDeRed) {
        const [cacheViajes, cacheClientes] = await Promise.all([leerViajes(), leerClientes()]);
        // Los clientes del cache sirven para registrar entregas offline
        if (cacheClientes.length > 0) setClientes(cacheClientes);
        if (cacheViajes.length > 0) {
          setViajes(cacheViajes);
          setError('');
        } else {
          setError('Sin conexión y sin datos guardados en este dispositivo.');
        }
      } else {
        setError('Error cargando viajes');
      }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); refrescarPendientes(); }, [cargar, refrescarPendientes]);

  // Al recuperar la conexión: PRIMERO sincronizar la cola, DESPUÉS recargar.
  // (Recargar antes de sincronizar borraría de la vista las entregas ⏳ que
  //  aún no llegaron al servidor.)
  useEffect(() => {
    if (online) {
      programarReintentos(api, (resumen) => {
        refrescarPendientes();
        if (!resumen) return;
        // Recargar del servidor SOLO cuando ya no quedan pendientes,
        // para no pisar entregas ⏳ que todavía no se enviaron.
        if (resumen.restantes === 0) {
          cargar();
        }
      });
    } else {
      detenerReintentos();
    }
    return () => detenerReintentos();
  }, [online]); // eslint-disable-line

  const resetFormViaje = () => setFormViaje({
    vehiculo_id: '', conductor_id: '', origen_id: '',
    tipo_combustible_id: '', fecha_salida: new Date().toISOString().slice(0,16),
    galones_declarados: '', dieta: '', observaciones: ''
  });

  // Convierte cualquier error de axios en un texto seguro para mostrar.
  // El 422 de FastAPI trae detail como array de objetos {type,loc,msg,input}
  // que NO se puede renderizar directo (rompe React con error #31).
  const mensajeError = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) {
      return d.map(x => {
        const campo = Array.isArray(x.loc) ? x.loc[x.loc.length - 1] : '';
        return campo ? `${campo}: ${x.msg}` : x.msg;
      }).join(' · ');
    }
    return fallback;
  };

  const crearViaje = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const payload = {
        vehiculo_id: formViaje.vehiculo_id,
        conductor_id: formViaje.conductor_id,
        origen_id: formViaje.origen_id,
        tipo_combustible_id: formViaje.tipo_combustible_id,
        fecha_salida: new Date(formViaje.fecha_salida).toISOString(),
        galones_declarados: parseFloat(formViaje.galones_declarados) || 0,
        observaciones: formViaje.observaciones || null,
      };
      // La dieta solo la maneja el admin
      if (puedeDinero) {
        payload.dieta = formViaje.dieta === '' || formViaje.dieta == null ? 0 : parseFloat(formViaje.dieta);
      }
      let res;
      if (editandoId) {
        res = await api.patch(`/viajes/${editandoId}`, payload);
      } else {
        res = await api.post('/viajes/', payload);
      }
      setViajeActual(res.data);
      setEntregas(res.data.entregas || []);
      setEditandoId(null);
      setVista('detalle');
      cargar();
    } catch (err) { setError(mensajeError(err, editandoId ? 'Error editando viaje' : 'Error creando viaje')); }
    finally { setSaving(false); }
  };

  const abrirEdicion = (viaje) => {
    setEditandoId(viaje.id);
    setFormViaje({
      vehiculo_id: viaje.vehiculo_id || '',
      conductor_id: viaje.conductor_id || '',
      origen_id: viaje.origen_id || '',
      tipo_combustible_id: viaje.tipo_combustible_id || '',
      fecha_salida: viaje.fecha_salida ? new Date(viaje.fecha_salida).toISOString().slice(0,16) : '',
      galones_declarados: viaje.galones_declarados || '',
      dieta: viaje.dieta || '',
      observaciones: viaje.observaciones || '',
    });
    setVista('nuevo');
  };

  const abrirDetalle = async (viaje) => {
    try {
      const res = await api.get('/viajes');
      const v = res.data.find(x => x.id === viaje.id) || viaje;
      setViajeActual(v);
      setEntregas(v.entregas || []);
      setVista('detalle');
    } catch { setViajeActual(viaje); setVista('detalle'); }
  };

  const agregarEntrega = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    const payload = {
      cliente_id: formEntrega.cliente_id,
      galones_entregados: parseFloat(formEntrega.galones_entregados) || 0,
      observaciones: formEntrega.observaciones || null,
    };
    try {
      const res = await api.post(`/viajes/${viajeActual.id}/entregas`, payload);
      setEntregas(prev => [...prev, res.data]);
      setFormEntrega({ cliente_id: '', galones_entregados: '', precio_galon: '', observaciones: '' });
    } catch (err) {
      // Si falló por falta de conexión, encolar la entrega para enviarla luego
      const esErrorDeRed = !err?.response;
      if (esErrorDeRed) {
        const item = await encolar({
          tipo: 'entrega',
          viaje_id: viajeActual.id,
          payload,
        });
        if (item) {
          // Mostrarla en la lista marcada como pendiente (⏳), con su temp_id
          setEntregas(prev => [...prev, {
            id: item.temp_id,
            _pendiente: true,
            cliente_id: payload.cliente_id,
            galones_entregados: payload.galones_entregados,
            observaciones: payload.observaciones,
            precio_galon: null,
            total: 0,
          }]);
          setFormEntrega({ cliente_id: '', galones_entregados: '', precio_galon: '', observaciones: '' });
          refrescarPendientes();
        } else {
          setError('No se pudo guardar la entrega offline en este dispositivo.');
        }
      } else {
        setError(mensajeError(err, 'Error agregando entrega'));
      }
    }
    finally { setSaving(false); }
  };

  const cerrarViaje = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const payload = {
        galones_reales: parseFloat(formCerrar.galones_reales) || 0,
        fecha_retorno: new Date(formCerrar.fecha_retorno).toISOString(),
        precio_galon_excedente: formCerrar.precio_galon_excedente === '' || formCerrar.precio_galon_excedente == null ? 0 : parseFloat(formCerrar.precio_galon_excedente),
        observaciones: formCerrar.observaciones || null,
      };
      await api.post(`/viajes/${viajeActual.id}/cerrar`, payload);
      setVista('lista');
      cargar();
    } catch (err) { setError(mensajeError(err, 'Error cerrando viaje')); }
    finally { setSaving(false); }
  };

  const aceptarViaje = async (viaje) => {
    setError(''); setSaving(true);
    try {
      const res = await api.patch(`/viajes/${viaje.id}/aceptar`);
      setViajeActual(res.data);
      setEntregas(res.data.entregas || []);
      setVista('detalle');
      cargar();
    } catch (err) { setError(mensajeError(err, 'Error aceptando viaje')); }
    finally { setSaving(false); }
  };

  const ponerPrecio = async (entregaId, precio) => {
    setError('');
    try {
      const res = await api.patch(`/viajes/${viajeActual.id}/entregas/${entregaId}/precio`, { precio_galon: parseFloat(precio) });
      setEntregas(prev => prev.map(e => e.id === entregaId ? res.data : e));
    } catch (err) { setError(mensajeError(err, 'Error poniendo precio')); }
  };

  const totalEntregas = entregas.reduce((s, e) => s + parseFloat(e.galones_entregados || 0), 0);
  const galonesDeclarados = parseFloat(viajeActual?.galones_declarados || 0);
  const disponible = galonesDeclarados - totalEntregas;

  const getNombre = (lista, id, campo = 'nombre') => lista.find(x => x.id === id)?.[campo] || id?.slice(0,8) || '-';
  // Nombre preferente: el que ya trae el viaje (backend), o resuelto desde catálogo
  const nombreViaje = (v, campoNombre, lista, id, campoLista) => v?.[campoNombre] || getNombre(lista, id, campoLista);

  if (loading) return <Layout usuario={usuario} onLogout={onLogout} title="Viajes"><div style={S.loading}>Cargando...</div></Layout>;

  // ── LISTA ──
  if (vista === 'lista') return (
    <Layout usuario={usuario} onLogout={onLogout} title="Viajes">
      <BannerOffline online={online} pendientes={pendientes} />
      <div style={S.headerRow}>
        <div style={S.tabs}>
          <span style={S.stat}><b>{viajes.filter(v=>v.estado==='pendiente').length}</b> Pendientes</span>
          <span style={S.stat}><b>{viajes.filter(v=>v.estado==='aceptado' || v.estado==='en_curso').length}</b> Aceptados</span>
          <span style={S.stat}><b>{viajes.filter(v=>v.estado==='completado').length}</b> Completados</span>
        </div>
        {(esAdmin || esDespachador) && (
          <button style={S.btnAdd} onClick={() => { setEditandoId(null); resetFormViaje(); setVista('nuevo'); }}>+ Nuevo Viaje</button>
        )}
      </div>
      {error && <div style={S.error}>{error}</div>}
      {viajes.length === 0 ? (
        <div style={S.empty}><div style={{fontSize:'48px'}}>🚛</div><p>No hay viajes registrados.</p></div>
      ) : (
        <div style={S.list}>
          {viajes.map(v => {
            const est = ESTADO_CONFIG[v.estado] || {};
            return (
              <div key={v.id} style={S.card} onClick={() => abrirDetalle(v)}>
                <div style={S.cardLeft}>
                  <div style={S.cardTop}>
                    <span style={{...S.badge, background: est.bg, color: est.color}}>{est.label}</span>
                    <span style={S.fecha}>{new Date(v.fecha_salida).toLocaleDateString('es-DO')}</span>
                  </div>
                  <div style={S.cardInfo}>
                    <span>🚛 {nombreViaje(v, 'vehiculo_nombre', vehiculos, v.vehiculo_id, 'placa')}</span>
                    <span>👤 {nombreViaje(v, 'conductor_nombre', conductores, v.conductor_id)}</span>
                    <span>⛽ {v.galones_declarados} gal</span>
                    <span>📍 {nombreViaje(v, 'origen_nombre', origenes, v.origen_id)}</span>
                  </div>
                </div>
                <div style={S.cardArrow}>›</div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );

  // ── NUEVO / EDITAR VIAJE ──
  if (vista === 'nuevo') return (
    <Layout usuario={usuario} onLogout={onLogout} title={editandoId ? 'Editar Viaje' : 'Nuevo Viaje'}>
      {error && <div style={S.error}>{error}</div>}
      <form onSubmit={crearViaje} style={S.form}>
        <div style={S.formGrid}>
          <div style={S.group}>
            <label style={S.label}>Vehículo / Cisterna *</label>
            <select style={S.input} value={formViaje.vehiculo_id} onChange={e => setFormViaje({...formViaje, vehiculo_id: e.target.value})} required>
              <option value="">Seleccionar...</option>
              {vehiculos.map(v => <option key={v.id} value={v.id}>{v.placa} — {v.capacidad_galones} gal</option>)}
            </select>
          </div>
          <div style={S.group}>
            <label style={S.label}>Conductor *</label>
            <select style={S.input} value={formViaje.conductor_id} onChange={e => setFormViaje({...formViaje, conductor_id: e.target.value})} required>
              <option value="">Seleccionar...</option>
              {conductores.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div style={S.group}>
            <label style={S.label}>Origen de Carga *</label>
            <select style={S.input} value={formViaje.origen_id} onChange={e => setFormViaje({...formViaje, origen_id: e.target.value})} required>
              <option value="">Seleccionar...</option>
              {origenes.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
            </select>
          </div>
          <div style={S.group}>
            <label style={S.label}>Tipo de Combustible *</label>
            <select style={S.input} value={formViaje.tipo_combustible_id} onChange={e => setFormViaje({...formViaje, tipo_combustible_id: e.target.value})} required>
              <option value="">Seleccionar...</option>
              {tiposCombustible.map(t => <option key={t.id} value={t.id}>{puedeDinero ? `${t.nombre} — RD$${t.precio_galon}/gal` : t.nombre}</option>)}
            </select>
          </div>
          <div style={S.group}>
            <label style={S.label}>Galones Declarados *</label>
            <input style={S.input} type="number" step="0.01" min="0" value={formViaje.galones_declarados} onChange={e => setFormViaje({...formViaje, galones_declarados: e.target.value})} placeholder="Ej: 5000" required />
          </div>
          <div style={S.group}>
            <label style={S.label}>Fecha y Hora de Salida *</label>
            <input style={S.input} type="datetime-local" value={formViaje.fecha_salida} onChange={e => setFormViaje({...formViaje, fecha_salida: e.target.value})} required />
          </div>
          {puedeDinero && (
            <div style={S.group}>
              <label style={S.label}>Dieta del Conductor (RD$)</label>
              <input style={S.input} type="number" step="0.01" min="0" value={formViaje.dieta} onChange={e => setFormViaje({...formViaje, dieta: e.target.value})} placeholder="Opcional (solo viajes lejanos)" />
            </div>
          )}
          <div style={{...S.group, gridColumn:'1 / -1'}}>
            <label style={S.label}>Observaciones</label>
            <input style={S.input} value={formViaje.observaciones} onChange={e => setFormViaje({...formViaje, observaciones: e.target.value})} placeholder="Opcional..." />
          </div>
        </div>
        <div style={S.formBtns}>
          <button type="button" style={S.btnCancel} onClick={() => { setEditandoId(null); setVista(editandoId ? 'detalle' : 'lista'); }}>Cancelar</button>
          <button type="submit" style={S.btnSave} disabled={saving}>{saving ? 'Guardando...' : (editandoId ? '💾 Guardar Cambios' : '🚛 Iniciar Viaje')}</button>
        </div>
      </form>
    </Layout>
  );

  // ── DETALLE VIAJE ──
  if (vista === 'detalle' && viajeActual) {
    const est = ESTADO_CONFIG[viajeActual.estado] || {};
    const esPendiente = viajeActual.estado === 'pendiente';
    const esOperable = viajeActual.estado === 'aceptado' || viajeActual.estado === 'en_curso';
    // El conductor solo opera sus propios viajes; el admin opera todos; el despachador NO opera
    const puedeOperar = esAdmin || esConductor;
    return (
      <Layout usuario={usuario} onLogout={onLogout} title="Detalle del Viaje">
        <BannerOffline online={online} pendientes={pendientes} />
        {error && <div style={S.error}>{error}</div>}

        {/* Info viaje */}
        <div style={S.block}>
          <div style={S.blockHeader}>
            <span style={{...S.badge, background: est.bg, color: est.color, fontSize:'13px', padding:'4px 14px'}}>{est.label}</span>
            <div style={{display:'flex', gap:'8px'}}>
              {(esPendiente || esOperable) && (esAdmin || esDespachador) && (
                <button style={{...S.btnSave, padding:'8px 16px', fontSize:'13px', background:'#6c757d'}} onClick={() => abrirEdicion(viajeActual)} disabled={saving}>
                  ✏️ Editar
                </button>
              )}
              {esPendiente && puedeOperar && (
                <button style={{...S.btnSave, padding:'8px 16px', fontSize:'13px', background:'#055160'}} onClick={() => aceptarViaje(viajeActual)} disabled={saving}>
                  ✓ Aceptar Viaje
                </button>
              )}
              {esOperable && puedeOperar && (
                <button style={{...S.btnSave, padding:'8px 16px', fontSize:'13px'}} onClick={() => { setFormCerrar({galones_reales:'', precio_galon_excedente:'', fecha_retorno: new Date().toISOString().slice(0,16), observaciones:''}); setVista('cerrar'); }}>
                  🔒 Cerrar Viaje
                </button>
              )}
            </div>
          </div>
          <div style={S.infoGrid}>
            <div style={S.infoItem}><span style={S.infoLabel}>🚛 Vehículo</span><span style={S.infoVal}>{nombreViaje(viajeActual, 'vehiculo_nombre', vehiculos, viajeActual.vehiculo_id, 'placa')}</span></div>
            <div style={S.infoItem}><span style={S.infoLabel}>👤 Conductor</span><span style={S.infoVal}>{nombreViaje(viajeActual, 'conductor_nombre', conductores, viajeActual.conductor_id)}</span></div>
            <div style={S.infoItem}><span style={S.infoLabel}>📍 Origen</span><span style={S.infoVal}>{nombreViaje(viajeActual, 'origen_nombre', origenes, viajeActual.origen_id)}</span></div>
            <div style={S.infoItem}><span style={S.infoLabel}>⛽ Combustible</span><span style={S.infoVal}>{nombreViaje(viajeActual, 'tipo_combustible_nombre', tiposCombustible, viajeActual.tipo_combustible_id)}</span></div>
            <div style={S.infoItem}><span style={S.infoLabel}>📦 Gal. Declarados</span><span style={{...S.infoVal, color:'#CC0000', fontWeight:'800'}}>{viajeActual.galones_declarados} gal</span></div>
            <div style={S.infoItem}><span style={S.infoLabel}>📅 Fecha Salida</span><span style={S.infoVal}>{new Date(viajeActual.fecha_salida).toLocaleString('es-DO')}</span></div>
          </div>
          {esPendiente && (
            <div style={{marginTop:'12px', padding:'10px 14px', background:'#f8f9fa', borderRadius:'8px', fontSize:'12px', color:'#666', textAlign:'center'}}>
              {puedeOperar ? 'Este viaje está pendiente. Acéptalo para poder registrar entregas.' : 'Este viaje está pendiente de que el conductor lo acepte.'}
            </div>
          )}
        </div>

        {/* Disponible del viaje (baja con cada entrega) */}
        <div style={S.dispBar}>
          <div style={S.dispItem}>
            <span style={S.dispLabel}>📦 Declarado</span>
            <span style={S.dispValNeutro}>{galonesDeclarados.toFixed(2)} gal</span>
          </div>
          <div style={S.dispSep}>−</div>
          <div style={S.dispItem}>
            <span style={S.dispLabel}>🚚 Entregado</span>
            <span style={S.dispValNeutro}>{totalEntregas.toFixed(2)} gal</span>
          </div>
          <div style={S.dispSep}>=</div>
          <div style={S.dispItem}>
            <span style={S.dispLabel}>⛽ Disponible</span>
            <span style={{...S.dispValFuerte, color: disponible <= 0 ? '#999' : '#CC0000'}}>{disponible.toFixed(2)} gal</span>
          </div>
        </div>

        {/* Entregas */}
        <div style={S.block}>
          <div style={S.blockHeader}>
            <h3 style={S.blockTitle}>Entregas ({entregas.length})</h3>
            <span style={S.totalEntregas}>Total: {totalEntregas.toFixed(2)} gal</span>
          </div>

          {entregas.length === 0 ? (
            <div style={{padding:'16px', color:'#888', fontSize:'13px', textAlign:'center'}}>No hay entregas registradas aún.</div>
          ) : (
            entregas.map((ent, i) => {
              const sinPrecio = !ent.precio_galon || parseFloat(ent.precio_galon) === 0;
              return (
              <div key={ent.id || i} style={S.entregaRow}>
                <div style={S.entregaLeft}>
                  <div style={S.entregaCliente}>
                    🏢 {getNombre(clientes, ent.cliente_id)}
                    {ent._pendiente && <span style={{color:'#b8860b', fontWeight:'700', marginLeft:'6px'}}>⏳ Sin enviar</span>}
                  </div>
                  <div style={S.entregaInfo}>
                    {ent.galones_entregados} gal
                    {puedeDinero && !sinPrecio && ` · RD$${ent.precio_galon}/gal`}
                    {puedeDinero && sinPrecio && !ent._pendiente && <span style={{color:'#b8860b', fontWeight:'600'}}> · ⚠️ Precio pendiente</span>}
                  </div>
                </div>
                {puedeDinero && !ent._pendiente && (
                  sinPrecio ? (
                    <button style={S.btnPrecio} onClick={() => {
                      const p = window.prompt(`Precio por galón para ${getNombre(clientes, ent.cliente_id)} (${ent.galones_entregados} gal):`, '');
                      if (p && !isNaN(parseFloat(p))) ponerPrecio(ent.id, p);
                    }}>✏️ Poner precio</button>
                  ) : (
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <div style={S.entregaTotal}>RD$ {parseFloat(ent.total || 0).toLocaleString('es-DO', {minimumFractionDigits:2})}</div>
                      <button style={S.btnPrecioMini} onClick={() => {
                        const p = window.prompt(`Nuevo precio por galón:`, ent.precio_galon);
                        if (p && !isNaN(parseFloat(p))) ponerPrecio(ent.id, p);
                      }}>✏️</button>
                    </div>
                  )
                )}
              </div>
              );
            })
          )}

          {/* Aviso sin combustible disponible */}
          {esOperable && disponible <= 0 && (
            <div style={S.sinDisp}>
              ⛽ No queda combustible disponible en este viaje. Se entregaron los {galonesDeclarados.toFixed(2)} gal declarados.
            </div>
          )}

          {/* Agregar entrega — solo conductor dueño o admin, en viaje operable */}
          {esOperable && puedeOperar && disponible > 0 && (
            <div style={S.addEntrega}>
              <div style={{fontSize:'13px', fontWeight:'700', color:'#1a1a1a', marginBottom:'12px'}}>+ Agregar Entrega <span style={{color:'#CC0000', fontWeight:'800'}}>· Disponible: {disponible.toFixed(2)} gal</span></div>
              <form onSubmit={agregarEntrega}>
                <div style={S.formGrid}>
                  <div style={S.group}>
                    <label style={S.label}>Cliente *</label>
                    <select style={S.input} value={formEntrega.cliente_id} onChange={e => setFormEntrega({...formEntrega, cliente_id: e.target.value})} required>
                      <option value="">Seleccionar...</option>
                      {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                  <div style={S.group}>
                    <label style={S.label}>Galones entregados * <span style={{color:'#888', fontWeight:'400'}}>(máx {disponible.toFixed(2)})</span></label>
                    <input style={S.input} type="number" step="0.01" min="0" max={disponible} value={formEntrega.galones_entregados} onChange={e => setFormEntrega({...formEntrega, galones_entregados: e.target.value})} placeholder={`Máx ${disponible.toFixed(2)}`} required />
                  </div>
                  <div style={S.group}>
                    <label style={S.label}>Observaciones</label>
                    <input style={S.input} value={formEntrega.observaciones} onChange={e => setFormEntrega({...formEntrega, observaciones: e.target.value})} placeholder="Opcional" />
                  </div>
                </div>
                <button type="submit" style={{...S.btnSave, width:'100%', marginTop:'8px'}} disabled={saving}>
                  {saving ? 'Guardando...' : '✅ Registrar Entrega'}
                </button>
              </form>
            </div>
          )}
        </div>

        <button style={{...S.btnCancel, width:'100%', marginTop:'8px'}} onClick={() => { setVista('lista'); setViajeActual(null); }}>
          ← Volver a lista
        </button>
      </Layout>
    );
  }

  // ── CERRAR VIAJE ──
  if (vista === 'cerrar' && viajeActual) return (
    <Layout usuario={usuario} onLogout={onLogout} title="Cerrar Viaje">
      <div style={{...S.block, borderColor:'#CC0000', background:'#fff8f8'}}>
        <div style={{fontSize:'13px', color:'#CC0000', fontWeight:'700', marginBottom:'8px'}}>⚠️ Al cerrar el viaje se calculará el excedente automáticamente</div>
        <div style={{fontSize:'12px', color:'#888'}}>
          Galones declarados: <b>{viajeActual.galones_declarados} gal</b> · 
          Entregas registradas: <b>{(viajeActual.entregas || []).length}</b>
        </div>
      </div>
      {error && <div style={S.error}>{error}</div>}
      <form onSubmit={cerrarViaje} style={S.form}>
        <div style={S.formGrid}>
          <div style={S.group}>
            <label style={S.label}>Galones reales cargados *</label>
            <input style={{...S.input, borderColor:'#CC0000'}} type="number" step="0.01" min="0" value={formCerrar.galones_reales} onChange={e => setFormCerrar({...formCerrar, galones_reales: e.target.value})} placeholder="Galones reales en cisterna" required />
          </div>
          {puedeDinero && (
            <div style={S.group}>
              <label style={S.label}>Precio galón excedente RD$ <span style={{color:'#888', fontWeight:'400'}}>(opcional)</span></label>
              <input style={{...S.input, borderColor:'#CC0000'}} type="number" step="0.01" min="0" value={formCerrar.precio_galon_excedente} onChange={e => setFormCerrar({...formCerrar, precio_galon_excedente: e.target.value})} placeholder="Puedes ponerlo después" />
            </div>
          )}
          <div style={S.group}>
            <label style={S.label}>Fecha y Hora de Retorno *</label>
            <input style={S.input} type="datetime-local" value={formCerrar.fecha_retorno} onChange={e => setFormCerrar({...formCerrar, fecha_retorno: e.target.value})} required />
          </div>
          <div style={S.group}>
            <label style={S.label}>Observaciones</label>
            <input style={S.input} value={formCerrar.observaciones} onChange={e => setFormCerrar({...formCerrar, observaciones: e.target.value})} placeholder="Opcional" />
          </div>
        </div>

        {/* Preview excedente — solo admin */}
        {puedeDinero && formCerrar.galones_reales && formCerrar.precio_galon_excedente && (
          <div style={S.previewExc}>
            <div style={S.previewTitle}>Preview del excedente (estimado)</div>
            <div style={S.previewRow}>
              <span>Galones excedente:</span>
              <span>{(parseFloat(formCerrar.galones_reales) - parseFloat(viajeActual.galones_declarados)).toFixed(2)} gal</span>
            </div>
            <div style={S.previewRow}>
              <span>Total excedente:</span>
              <span>RD$ {((parseFloat(formCerrar.galones_reales) - parseFloat(viajeActual.galones_declarados)) * parseFloat(formCerrar.precio_galon_excedente)).toLocaleString('es-DO', {minimumFractionDigits:2})}</span>
            </div>
          </div>
        )}

        <div style={S.formBtns}>
          <button type="button" style={S.btnCancel} onClick={() => setVista('detalle')}>Cancelar</button>
          <button type="submit" style={{...S.btnSave, background:'linear-gradient(135deg,#990000,#660000)'}} disabled={saving}>
            {saving ? 'Cerrando...' : '🔒 Confirmar Cierre'}
          </button>
        </div>
      </form>
    </Layout>
  );

  return null;
};

const S = {
  loading: {textAlign:'center', padding:'60px', color:'#666'},
  headerRow: {display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'10px'},
  tabs: {display:'flex', gap:'16px'},
  stat: {fontSize:'13px', color:'#666', fontWeight:'500'},
  btnAdd: {background:'#CC0000', color:'#fff', border:'none', borderRadius:'8px', padding:'10px 18px', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
  error: {background:'#fff0f0', border:'1px solid #ffcccc', color:'#CC0000', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px'},
  empty: {textAlign:'center', padding:'60px', color:'#999', background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0'},
  list: {display:'flex', flexDirection:'column', gap:'8px'},
  card: {background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0', padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', boxShadow:'0 2px 6px rgba(0,0,0,0.05)'},
  cardLeft: {flex:1},
  cardTop: {display:'flex', alignItems:'center', gap:'10px', marginBottom:'6px'},
  badge: {display:'inline-block', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'700'},
  fecha: {fontSize:'12px', color:'#888'},
  cardInfo: {display:'flex', gap:'12px', flexWrap:'wrap', fontSize:'12px', color:'#555'},
  cardArrow: {fontSize:'20px', color:'#ccc', marginLeft:'8px'},
  form: {display:'flex', flexDirection:'column', gap:'16px'},
  formGrid: {display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px'},
  group: {display:'flex', flexDirection:'column', gap:'6px'},
  label: {fontSize:'12px', fontWeight:'600', color:'#555'},
  input: {padding:'10px 12px', borderRadius:'7px', border:'1.5px solid #e0e0e0', fontSize:'14px', outline:'none', color:'#1a1a1a', background:'#fff'},
  formBtns: {display:'flex', gap:'10px', justifyContent:'flex-end'},
  btnCancel: {padding:'10px 20px', borderRadius:'7px', border:'1.5px solid #e0e0e0', background:'#fff', color:'#666', fontSize:'13px', fontWeight:'600', cursor:'pointer'},
  btnSave: {padding:'10px 20px', borderRadius:'7px', border:'none', background:'#CC0000', color:'#fff', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
  block: {background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0', padding:'16px', marginBottom:'14px', boxShadow:'0 2px 6px rgba(0,0,0,0.05)'},
  blockHeader: {display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'},
  blockTitle: {fontSize:'14px', fontWeight:'700', color:'#1a1a1a'},
  infoGrid: {display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'},
  infoItem: {display:'flex', flexDirection:'column', gap:'2px'},
  infoLabel: {fontSize:'11px', color:'#888', fontWeight:'500'},
  infoVal: {fontSize:'13px', color:'#1a1a1a', fontWeight:'600'},
  totalEntregas: {fontSize:'13px', fontWeight:'700', color:'#CC0000'},
  entregaRow: {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderTop:'1px solid #f0f0f0'},
  entregaLeft: {},
  entregaCliente: {fontSize:'13px', fontWeight:'600', color:'#1a1a1a'},
  entregaInfo: {fontSize:'12px', color:'#888', marginTop:'2px'},
  entregaTotal: {fontSize:'14px', fontWeight:'700', color:'#1a1a1a'},
  addEntrega: {background:'#f8f8f8', borderRadius:'10px', padding:'16px', marginTop:'12px'},
  previewExc: {background:'#fff0f0', border:'1px solid #ffcccc', borderRadius:'10px', padding:'14px', marginTop:'4px'},
  previewTitle: {fontSize:'12px', fontWeight:'700', color:'#CC0000', marginBottom:'8px'},
  previewRow: {display:'flex', justifyContent:'space-between', fontSize:'13px', color:'#333', padding:'3px 0'},
  dispBar: {display:'flex', alignItems:'center', justifyContent:'center', gap:'14px', flexWrap:'wrap', background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0', padding:'14px 16px', marginBottom:'14px', boxShadow:'0 2px 6px rgba(0,0,0,0.05)'},
  dispItem: {display:'flex', flexDirection:'column', alignItems:'center', gap:'2px', minWidth:'90px'},
  dispLabel: {fontSize:'11px', color:'#888', fontWeight:'500'},
  dispValNeutro: {fontSize:'15px', color:'#1a1a1a', fontWeight:'700'},
  dispValFuerte: {fontSize:'18px', fontWeight:'800'},
  dispSep: {fontSize:'18px', color:'#ccc', fontWeight:'700'},
  sinDisp: {background:'#f3f3f3', border:'1px solid #e0e0e0', borderRadius:'10px', padding:'14px', marginTop:'12px', fontSize:'13px', color:'#666', textAlign:'center', fontWeight:'600'},
  btnPrecio: {background:'#fff8e1', border:'1.5px solid #ffd54f', color:'#b8860b', borderRadius:'7px', padding:'7px 12px', fontSize:'12px', fontWeight:'700', cursor:'pointer'},
  btnPrecioMini: {background:'#fff', border:'1px solid #e0e0e0', borderRadius:'6px', padding:'4px 8px', fontSize:'12px', cursor:'pointer'},
};

export default Viajes;
